import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthProvider";
import { getStripe } from "@/lib/stripe";
import { track } from "@/lib/analytics";
import { apiFetch, ApiError } from "./client";
import type { LabCheckoutResponse, LabCheckoutStatus } from "@/types/labs";

export interface LabAcquisitionOffer {
  acquisition_id: string;
  experiment_key: string;
  variant: "variant-a";
  product_id: string;
  title: string;
  amount_cents: number;
  renewal_amount_cents: number;
  interval_months: number;
  measurement_count: number;
}

export interface LabAcquisition {
  acquisition_id: string;
  title: string;
  status: "pending" | "requires_action" | "paid" | "failed" | "cancelled";
  amount_cents: number;
  renewal_amount_cents: number;
  interval_months: number;
  checkout_id?: string | null;
  lab_order_id?: string | null;
}

type AcquisitionPayment = LabCheckoutResponse & { checkout_token: string };
export interface LabAcquisitionPromotion {
  cart_promotion_id: string;
  cart_promotion_line_key: string;
  expected_amount_cents: number;
}
interface Attempt {
  request_id: string;
  checkout_token: string;
  payment_method_id: string;
  consent: true;
  expected_amount_cents: number;
  cart_promotion_id?: string;
  cart_promotion_line_key?: string;
}
const key = (owner: string, acquisition: string) =>
  `system.labs.acquisition.${owner}.${acquisition}`;
export function readLabAcquisitionAttempt(
  owner: string | undefined,
  acquisition: string,
): Attempt | null {
  if (!owner) return null;
  try {
    const value = JSON.parse(
      localStorage.getItem(key(owner, acquisition)) || "null",
    );
    return value?.request_id &&
      value?.checkout_token &&
      value?.payment_method_id
      ? value
      : null;
  } catch {
    return null;
  }
}

export function useLabAcquisitions() {
  const { session } = useAuth();
  const owner = session?.user.id;
  return useQuery({
    queryKey: ["me", "lab-acquisitions", owner],
    queryFn: () =>
      apiFetch<{ items: LabAcquisition[] }>("/me/labs/acquisition"),
    enabled: Boolean(owner),
    staleTime: 30_000,
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (item) =>
          item.status === "pending" ||
          (item.status === "paid" && !item.lab_order_id),
      )
        ? 5_000
        : false,
  });
}

/** Purchases and resumes use one owner-scoped identity across disconnects and
 * bank authentication. The acquisition endpoint owns eligibility and pricing. */
export function useLabAcquisitionPayment(acquisitionId: string) {
  const { session } = useAuth();
  const owner = session?.user.id;
  const current = useRef(owner);
  current.current = owner;
  const inFlight = useRef(false);
  const noCheckout = useRef(false);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const [needsPaymentMethod, setNeedsPaymentMethod] = useState(false);
  const [attempt, setAttempt] = useState(() =>
    readLabAcquisitionAttempt(owner, acquisitionId),
  );
  useEffect(() => {
    current.current = owner;
    setAttempt(readLabAcquisitionAttempt(owner, acquisitionId));
    setError(null);
    setPaid(false);
    setNeedsPaymentMethod(false);
    setBusy(false);
    inFlight.current = false;
    noCheckout.current = false;
    return () => {
      current.current = undefined;
    };
  }, [owner, acquisitionId]);

  async function run(
    offer?: LabAcquisitionOffer,
    paymentMethod?: string,
    replacementPaymentMethod?: string,
    promotion?: LabAcquisitionPromotion,
  ) {
    if (!owner || inFlight.current) return false;
    inFlight.current = true;
    noCheckout.current = false;
    setBusy(true);
    setError(null);
    try {
      if (!navigator.locks?.request)
        throw new Error(
          "Use an up-to-date browser to confirm this payment safely.",
        );
      return await navigator.locks.request(
        `system.portal.purchase.${owner}`,
        async () => {
          if (current.current !== owner) return false;
          let body: Attempt | undefined;
          if (offer) {
            body = readLabAcquisitionAttempt(owner, acquisitionId) ?? {
              request_id: crypto.randomUUID(),
              checkout_token:
                crypto.randomUUID().replace(/-/g, "") +
                crypto.randomUUID().replace(/-/g, ""),
              payment_method_id: paymentMethod ?? "",
              consent: true,
              expected_amount_cents: offer.amount_cents,
              ...promotion,
            };
            if (
              body.expected_amount_cents !== (promotion?.expected_amount_cents ?? offer.amount_cents) ||
              (body.cart_promotion_id ?? null) !== (promotion?.cart_promotion_id ?? null)
            ) throw new Error("An earlier blood test payment has different terms. Continue to your portal to resume that saved payment before making another selection.");
            if (!body.payment_method_id)
              throw new Error("Choose a payment method before confirming.");
            // Fail closed if retry identity cannot be retained.
            localStorage.setItem(
              key(owner, acquisitionId),
              JSON.stringify(body),
            );
            setAttempt(body);
            track("blood_test_checkout_started", {
              source: "post_purchase_upsell",
              experiment_key: offer.experiment_key,
              variant_key: offer.variant,
              product_id: offer.product_id,
            });
          }
          const response = await apiFetch<AcquisitionPayment>(
            `/me/labs/acquisition/${encodeURIComponent(acquisitionId)}/${offer ? "purchase" : "resume"}`,
            { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) },
          );
          if (current.current !== owner) return false;
          let result: LabCheckoutResponse | LabCheckoutStatus = response;
          if (response.status !== "paid") {
            if (
              response.payment_status === "requires_payment_method" &&
              !replacementPaymentMethod
            ) {
              setNeedsPaymentMethod(true);
              throw new Error(
                "Your card was not accepted. Update your payment method in Account, then retry this saved payment.",
              );
            }
            if (
              (response.payment_status === "requires_action" ||
                (response.payment_status === "requires_payment_method" &&
                  replacementPaymentMethod)) &&
              response.client_secret
            ) {
              const stripe = await getStripe(response.publishable_key);
              if (current.current !== owner) return false;
              if (!stripe)
                throw new Error(
                  "Card verification could not load. Resume this payment to try again.",
                );
              const verification =
                response.payment_status === "requires_payment_method"
                  ? await stripe.confirmCardPayment(response.client_secret, {
                      payment_method: replacementPaymentMethod,
                    })
                  : await stripe.confirmCardPayment(response.client_secret);
              if (current.current !== owner) return false;
              if (verification.error) {
                if (
                  verification.error.payment_intent?.status ===
                  "requires_payment_method"
                )
                  setNeedsPaymentMethod(true);
                throw new Error(
                  verification.error.message ||
                    "Card verification is still required.",
                );
              }
              setNeedsPaymentMethod(false);
            }
            if (!response.checkout_token)
              throw new Error(
                "Payment status is not available yet. Resume this saved payment shortly.",
              );
            result = await apiFetch<LabCheckoutStatus>(
              "/labs/checkout/status",
              {
                method: "POST",
                body: JSON.stringify({
                  checkout_id: response.checkout_id,
                  checkout_token: response.checkout_token,
                }),
              },
            );
          }
          if (current.current !== owner) return false;
          if (result.payment_status === "requires_payment_method")
            setNeedsPaymentMethod(true);
          if (result.status === "paid") {
            localStorage.removeItem(key(owner, acquisitionId));
            setAttempt(null);
            setPaid(true);
            track("blood_test_confirmation_viewed", {
              source: offer ? "post_purchase_upsell" : "acquisition_recovery",
              acquisition_id: acquisitionId,
            });
            await Promise.allSettled([
              qc.invalidateQueries({ queryKey: ["me", "lab-orders"] }),
              qc.invalidateQueries({ queryKey: ["me", "lab-subscriptions"] }),
              qc.invalidateQueries({
                queryKey: ["me", "lab-acquisitions", owner],
              }),
            ]);
            return current.current === owner;
          }
          if (result.status === "cancelled" || result.status === "refunded") {
            localStorage.removeItem(key(owner, acquisitionId));
            setAttempt(null);
            throw new Error(
              "This payment is closed. Your treatment order is unchanged.",
            );
          }
          throw new Error(
            "Your blood test payment is still being checked. Resume it here before trying another purchase.",
          );
        },
      );
    } catch (cause) {
      if (current.current !== owner) return false;
      if (
        cause instanceof ApiError &&
        (cause.details as { checkout_not_created?: boolean } | null)
          ?.checkout_not_created
      ) {
        noCheckout.current = true;
        localStorage.removeItem(key(owner, acquisitionId));
        setAttempt(null);
      }
      track("blood_test_checkout_failed", {
        source: offer ? "post_purchase_upsell" : "acquisition_recovery",
        reason: cause instanceof ApiError ? cause.code : "payment_pending",
      });
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not complete your blood test payment. Resume it to check its status.",
      );
      return false;
    } finally {
      if (current.current === owner) {
        setBusy(false);
        inFlight.current = false;
      }
    }
  }
  return {
    busy,
    error,
    paid,
    attempt,
    needsPaymentMethod,
    checkoutNotCreated: () => current.current === owner && noCheckout.current,
    purchase: (offer: LabAcquisitionOffer, paymentMethod: string, promotion?: LabAcquisitionPromotion) =>
      run(offer, paymentMethod, needsPaymentMethod ? paymentMethod : undefined, promotion),
    resume: (replacementPaymentMethod?: string) =>
      run(undefined, undefined, replacementPaymentMethod),
  };
}
